<script setup lang="ts">
// Vue & Plugins
import { storeToRefs } from 'pinia'
import { defineAsyncComponent, provide, watch } from 'vue'

import { makeFormatters } from '#.shared/dates/utils/format'
import { DateFormattersKey } from '#.shared/di/keys.ts'
import { useNavigatorLanguage } from '@vueuse/core'
import { Settings } from 'luxon'
// Composables
import { usePrimeVue } from 'primevue/config'

// Components
import LoadingPage from './LoadingPage.vue'

import { useIsDark } from './common/composables/useIsDark'
import { useNavigation } from './navigation/composables/useNavigation'
import { useResendVerificationEmailMutation } from './shared/email_verification/composables/useEmailVerification'

import { useEventStore } from './common/eventStore'
import { useAuthUserStore } from './user/authUserStore'

import { EMAIL_VERIFICATION_TOAST_GROUP } from './shared/constants/toast'

const DevFeatureFlagPanel = import.meta.env.DEV
  ? defineAsyncComponent(
      () => import('./features/components/DevFeatureFlagPanel.vue'),
    )
  : null

// Data
// TODO: Remove inherit attrs flag if we dont need it.
//  This was added to fix a vue warning where props were being passed to the root element.
defineOptions({
  inheritAttrs: false,
})
provide(DateFormattersKey, makeFormatters(Settings.defaultLocale))

// TODO: Fix how we handle setting the locale, this is a temp fix and proof of concept for configuring outside of en/en-US
const { language } = useNavigatorLanguage()
watch(
  language,
  (value) => {
    const primeVue = usePrimeVue()

    if (value === 'en-GB') {
      if (primeVue.config.locale) {
        primeVue.config.locale.dateFormat = 'dd/mm/yy'
      }
    }
  },
  { immediate: true },
)

const { loadingMessage, showOverlay } = useNavigation()

const isDebug = import.meta.env.DEV
const { toggleDarkMode } = useIsDark()
const toggleColorScheme = () => {
  document.documentElement.classList.toggle('dark')

  toggleDarkMode()
}

const authUserStore = useAuthUserStore()
const { sessionExpired, passwordExpired } = storeToRefs(authUserStore)
const { clearSessionExpired, clearPasswordExpired } = authUserStore

const eventStore = useEventStore()
const { addSessionExpiredToast, addPasswordExpiredToast } = eventStore

watch(sessionExpired, (newSessionExpired) => {
  if (newSessionExpired) {
    addSessionExpiredToast()

    clearSessionExpired()
  }
})

watch(passwordExpired, (newPasswordExpired) => {
  if (newPasswordExpired) {
    addPasswordExpiredToast()

    clearPasswordExpired()
  }
})

const {
  mutate: resendVerificationEmail,
  isPending: isResendVerificationEmailPending,
  isSuccess: isResendVerificationEmailSuccess,
} = useResendVerificationEmailMutation()
</script>

<template>
  <component :is="DevFeatureFlagPanel" v-if="DevFeatureFlagPanel" />

  <!-- Dark Mode Toggle -->
  <Button
    v-if="isDebug"
    icon="pi pi-palette"
    aria-label="palette"
    class="fixed top-0 left-0 z-50 h-8 w-8 opacity-5 hover:opacity-100"
    @click="toggleColorScheme"
  />

  <Transition name="fade">
    <LoadingPage v-if="showOverlay" :message="loadingMessage" />
  </Transition>

  <router-view />

  <Toast />

  <Toast :group="EMAIL_VERIFICATION_TOAST_GROUP" position="top-center">
    <template #message="slotProps">
      <div>{{ slotProps.message.detail }}</div>

      <Button
        v-tooltip="isResendVerificationEmailSuccess ? 'Email Sent' : undefined"
        label="Resend"
        class="shrink-0"
        :loading="isResendVerificationEmailPending"
        :disabled="isResendVerificationEmailPending || isResendVerificationEmailSuccess"
        text
        @click="resendVerificationEmail()"
      />
    </template>
  </Toast>
</template>
